<template>
  <div class="gui-content">
    <h2>Restroom Selector</h2>
    <div class="dropdown-container" ref="dropdownContainer">
      <!-- Dropdown Headers -->
      <div class="dropdown-headers">
        <span
          class="dropdown-column"
          @click="sortBy('name')"
          :class="{ sortable: true, 'active-sort': sortKey === 'name' }"
        >
          <b>Name</b>
        </span>
        <span
          class="dropdown-column"
          @click="sortBy('date')"
          :class="{ sortable: true, 'active-sort': sortKey === 'date' }"
        >
          <b>Date</b>
        </span>
        <span
          class="dropdown-column"
          @click="sortBy('location')"
          :class="{ sortable: true, 'active-sort': sortKey === 'location' }"
        >
          <b>Location</b>
        </span>
      </div>
      <ul class="dropdown-menu">
        <li
          v-for="restroom in sortedRestrooms"
          :key="restroom.name"
          :class="{
            'active-restroom': selected && restroom.name === selected.name,
          }"
          @click="selectRestroom(restroom)"
          ref="dropdownItems"
        >
          <span class="dropdown-column">{{ restroom.name.toUpperCase() }}</span>
          <span class="dropdown-column">{{ restroom.date }}</span>
          <span class="dropdown-column">{{ restroom.location }}</span>
        </li>
      </ul>
    </div>
    <br />
    <!-- Randomize Button -->
    <button class="randomize-button" @click="emitRandomize">
      Randomize <span class="dice">🎲</span>
    </button>
    <!-- share button -->
    <div class="share-btn-wrapper">
      <button
        class="share-button"
        :class="{ active: shareActive }"
        @click="copyLink"
      >
        {{ copied ? 'Link copied!' : 'Share this restroom  🔗' }}
      </button>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'GUI-index',
    props: {
      restrooms: {
        type: Array,
        required: true,
      },
      selected: {
        type: Object,
        default: null,
      },
    },
    data() {
      return {
        sortKey: 'name',
        sortOrder: 'asc',
        copied: false,
        shareActive: false,
      };
    },
    computed: {
      sortedRestrooms() {
        return [...this.restrooms].sort((a, b) => {
          let keyA, keyB;

          if (this.sortKey === 'date') {
            keyA = new Date(a[this.sortKey]);
            keyB = new Date(b[this.sortKey]);
          } else if (this.sortKey === 'location') {
            // Split into [city, state]
            const [cityA = '', stateA = ''] = a.location
              .split(',')
              .map((s) => s.trim().toLowerCase());
            const [cityB = '', stateB = ''] = b.location
              .split(',')
              .map((s) => s.trim().toLowerCase());
            // Sort by state, then city
            if (stateA < stateB) return this.sortOrder === 'asc' ? -1 : 1;
            if (stateA > stateB) return this.sortOrder === 'asc' ? 1 : -1;
            if (cityA < cityB) return this.sortOrder === 'asc' ? -1 : 1;
            if (cityA > cityB) return this.sortOrder === 'asc' ? 1 : -1;
            return 0;
          } else {
            keyA = a[this.sortKey]?.toString().toLowerCase();
            keyB = b[this.sortKey]?.toString().toLowerCase();
          }

          if (keyA < keyB) return this.sortOrder === 'asc' ? -1 : 1;
          if (keyA > keyB) return this.sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      },
      shareUrl() {
        if (!this.selected || !this.selected.slug) return window.location.href;
        const base = window.location.origin + window.location.pathname;
        return `${base}#/${this.selected.slug}`;
      },
    },
    watch: {
      selected: {
        immediate: true,
        handler() {
          this.scrollToActiveRestroom();
        },
      },
    },
    methods: {
      emitRandomize() {
        this.$emit('randomize');
      },
      selectRestroom(restroom) {
        this.$emit('update:selectedRestroom', restroom);
      },
      scrollToActiveRestroom() {
        if (!this.selected || !this.selected.name) return;
        this.$nextTick(() => {
          const activeItem = this.$refs.dropdownItems?.find((item) =>
            item.textContent.includes(this.selected.name.toUpperCase())
          );
          if (activeItem) {
            activeItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      },
      sortBy(key) {
        if (this.sortKey === key) {
          this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
          this.sortKey = key;
          this.sortOrder = 'asc';
        }
      },
      copyLink() {
        navigator.clipboard.writeText(this.shareUrl).then(() => {
          this.copied = true;
          this.shareActive = true;
          setTimeout(() => {
            this.copied = false;
            this.shareActive = false;
          }, 1500);
        });
      },
    },
    mounted() {
      this.scrollToActiveRestroom();
    },
  };
</script>

<style scoped>
  h2 {
    font-family: satoshi;
    font-size: 16px;
    line-height: 0.5;
    color: var(--color-2-solid);
    text-shadow: 0 4px 15px rgba(0, 0, 0, 0.7);
    margin-left: 5px;
    text-align: center;
  }

  .gui-content {
    margin-top: 90px;
    margin-left: 5px;
    margin-right: 5px;
  }

  .dropdown-container {
    max-height: calc(100vh - 400px); /* adjust 150px to your header/spacing */
    height: auto;
    overflow-y: auto;
    overflow-x: hidden;
    border-radius: 5px;
    background: rgba(25, 25, 25, 0.41);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(6.5px);
    -webkit-backdrop-filter: blur(6.5px);
    border: 1px solid rgba(255, 255, 255, 0.13);
    position: relative;
  }

  .dropdown-headers {
    display: flex;
    top: 0;
    z-index: 2;
    position: sticky;
    justify-content: space-between;
    padding: 8px 10px;
    font-size: 14px;
    font-family: satoshi;
    color: rgba(0, 0, 0, 0.6);
    background-color: rgba(220, 220, 220, 0.9);
    backdrop-filter: blur(5.5px);
    cursor: pointer;
  }

  .dropdown-headers .sortable:hover {
    text-decoration: underline;
  }

  .dropdown-headers .active-sort {
    text-decoration: underline;
    color: black;
  }

  .dropdown-menu {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .dropdown-menu li {
    display: flex;
    justify-content: space-between;
    padding: 5px 10px;
    cursor: pointer;
    color: var(--color-2-solid);
    border-bottom: 1px solid rgba(255, 255, 255, 0.13);
    text-align: left;
    font-size: 10px;
    font-family: satoshi;
  }

  .dropdown-menu li:hover {
    background: rgba(255, 255, 255, 0.23);
    border: none;
  }

  .dropdown-menu li.active-restroom {
    background: rgba(255, 255, 255, 0.53);
    color: black;
  }

  .dropdown-column {
    flex: 1;
  }

  .randomize-button {
    font-family: satoshi;
    font-size: 14px;
    padding: 3px 10px;
    background: var(--color-2-transparent);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    border: 1px solid var(--color-2-transparent);
    color: var(--color-1-solid);
    border-radius: 5px;
    cursor: pointer;
    width: 100%;
  }

  .randomize-button:hover {
    background: rgba(25, 25, 25, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.43);
    color: var(--color-2-solid);
  }

  .dice {
    display: inline-block;
    transition: transform 0.4s;
  }
  .randomize-button:active .dice {
    transform: rotate(360deg);
  }

  .share-btn-wrapper {
    text-align: center;
    margin-top: 8px;
  }

  .share-button {
    width: 100%;
    min-width: 120px;
    font-family: satoshi;
    font-size: 14px;
    padding: 3px 10px;
    background: var(--color-1-transparent);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    border: 1px solid var(--color-2-transparent);
    color: var(--color-2-solid);
    border-radius: 5px;
    cursor: pointer;
  }
  .share-button:hover {
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.43);
    color: var(--color-1-solid);
  }

  .share-button.active {
    background: rgba(255, 0, 0, 0.9) !important;
    color: #fff !important;
    border: 1px solid white !important;
    transition: background 0.1s, color 0.1s;
  }

  @media (max-width: 600px) {
    .gui-content {
      margin-top: 70px;
    }

    .dropdown-container {
      max-height: calc(100vh - 500px);
    }

    .share-button {
      width: 100%;
      min-width: unset;
    }
  }
</style>
